import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Admin() {
  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
      <div className="small-label text-rtrl-red">League Owner</div>
      <h1 className="font-display uppercase text-5xl md:text-6xl text-white mt-2">Admin Console</h1>
      <Tabs defaultValue="syndicates" className="mt-8">
        <TabsList className="bg-surface border border-surface-line hud-chamfer-sm" data-testid="admin-tabs">
          <TabsTrigger value="syndicates" data-testid="admin-tab-syndicates">Syndicates</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="racers" data-testid="admin-tab-racers">Racer Links</TabsTrigger>
          <TabsTrigger value="points" data-testid="admin-tab-points">Points Table</TabsTrigger>
          <TabsTrigger value="tracks" data-testid="admin-tab-tracks">Race Tracks</TabsTrigger>
          <TabsTrigger value="scheduling" data-testid="admin-tab-scheduling">Scheduling</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="syndicates"><SyndicatesAdmin /></TabsContent>
        <TabsContent value="users"><UsersAdmin /></TabsContent>
        <TabsContent value="racers"><RacersAdmin /></TabsContent>
        <TabsContent value="points"><PointsAdmin /></TabsContent>
        <TabsContent value="tracks"><TracksAdmin /></TabsContent>
        <TabsContent value="scheduling"><SchedulingAdmin /></TabsContent>
        <TabsContent value="settings"><SettingsAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}

function SyndicatesAdmin() {
  const [syns, setSyns] = useState([]);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState({ name: "", tag: "", color: "#E51919", bio: "" });

  const load = () => {
    api.get("/syndicates").then((r) => setSyns(r.data));
    api.get("/auth/users").then((r) => setUsers(r.data));
  };
  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/syndicates", creating); setCreating({ name: "", tag: "", color: "#E51919", bio: "" }); toast.success("Syndicate created"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async (s) => {
    try {
      await api.patch(`/syndicates/${s.id}`, { name: s.name, tag: s.tag, color: s.color, bio: s.bio, owner_user_id: s.owner_user_id });
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-6 space-y-8">
      <form onSubmit={create} className="rtrl-card carbon-bg p-6 grid md:grid-cols-5 gap-4 items-end">
        <div><div className="small-label mb-1">Name</div><input required value={creating.name} onChange={(e)=>setCreating({...creating, name:e.target.value})} className="rtrl-input" data-testid="new-syn-name"/></div>
        <div><div className="small-label mb-1">Tag</div><input required maxLength={4} value={creating.tag} onChange={(e)=>setCreating({...creating, tag:e.target.value.toUpperCase()})} className="rtrl-input" data-testid="new-syn-tag"/></div>
        <div><div className="small-label mb-1">Color</div><input type="color" value={creating.color} onChange={(e)=>setCreating({...creating, color:e.target.value})} className="w-full h-11 bg-transparent border-b-2 border-surface-hi" data-testid="new-syn-color"/></div>
        <div className="md:col-span-2"><div className="small-label mb-1">Bio</div><input value={creating.bio} onChange={(e)=>setCreating({...creating, bio:e.target.value})} className="rtrl-input" data-testid="new-syn-bio"/></div>
        <button className="rtrl-btn md:col-span-5" data-testid="new-syn-submit">Create Syndicate</button>
      </form>

      <div className="space-y-3">
        {syns.map((s, i) => <SyndicateRow key={s.id} s={s} users={users} onSave={save} idx={i} />)}
      </div>
    </div>
  );
}

function SyndicateRow({ s, users, onSave, idx }) {
  const [edit, setEdit] = useState(s);
  useEffect(() => setEdit(s), [s.id]);
  return (
    <div className="rtrl-card carbon-bg p-4 grid md:grid-cols-6 gap-3 items-center" style={{ borderLeft: `4px solid ${edit.color}` }}>
      <input className="rtrl-input" value={edit.name} onChange={(e)=>setEdit({...edit, name:e.target.value})} data-testid={`syn-edit-name-${idx}`}/>
      <input className="rtrl-input" value={edit.tag} onChange={(e)=>setEdit({...edit, tag:e.target.value.toUpperCase()})} maxLength={4} data-testid={`syn-edit-tag-${idx}`}/>
      <input type="color" className="w-full h-10 bg-transparent" value={edit.color} onChange={(e)=>setEdit({...edit, color:e.target.value})} data-testid={`syn-edit-color-${idx}`}/>
      <input className="rtrl-input md:col-span-2" value={edit.bio || ""} onChange={(e)=>setEdit({...edit, bio:e.target.value})} data-testid={`syn-edit-bio-${idx}`}/>
      <select className="rtrl-input" value={edit.owner_user_id || ""} onChange={(e)=>setEdit({...edit, owner_user_id:e.target.value || null})} data-testid={`syn-edit-owner-${idx}`}>
        <option value="">— Owner —</option>
        {users.filter(u=>u.role==="syndicate_owner" || u.role==="gm").map(u=><option key={u.id} value={u.id}>{u.display_name} ({u.email})</option>)}
      </select>
      <button className="rtrl-btn md:col-span-6" onClick={()=>onSave(edit)} data-testid={`syn-save-${idx}`}>Save</button>
    </div>
  );
}

function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [syns, setSyns] = useState([]);
  const [form, setForm] = useState({ email:"", password:"", display_name:"", role:"syndicate_owner", syndicate_id:"" });

  const load = () => { api.get("/auth/users").then(r=>setUsers(r.data)); api.get("/syndicates").then(r=>setSyns(r.data)); };
  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/admin/create-user", { ...form, syndicate_id: form.syndicate_id || null });
      toast.success("User created");
      setForm({ email:"", password:"", display_name:"", role:"syndicate_owner", syndicate_id:"" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async (editUser) => {
    try {
      await api.patch(`/auth/users/${editUser.id}`, {
        display_name: editUser.display_name,
        role: editUser.role,
        syndicate_id: editUser.syndicate_id || null,
      });
      toast.success("User role and details updated successfully");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelete = async (uid) => {
    if (!window.confirm("Are you sure you want to permanently delete this user account?")) return;
    try {
      await api.delete(`/auth/users/${uid}`);
      toast.success("User account deleted successfully");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="mt-6 space-y-8">
      <form onSubmit={submit} className="rtrl-card carbon-bg p-6 grid md:grid-cols-6 gap-4 items-end">
        <div><div className="small-label mb-1">Display Name</div><input required value={form.display_name} onChange={(e)=>setForm({...form, display_name:e.target.value})} className="rtrl-input" data-testid="new-user-name"/></div>
        <div><div className="small-label mb-1">Email</div><input required type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="rtrl-input" data-testid="new-user-email"/></div>
        <div><div className="small-label mb-1">Password</div><input required minLength={6} value={form.password} onChange={(e)=>setForm({...form, password:e.target.value})} className="rtrl-input" data-testid="new-user-password"/></div>
        <div><div className="small-label mb-1">Role</div>
          <select value={form.role} onChange={(e)=>setForm({...form, role:e.target.value})} className="rtrl-input" data-testid="new-user-role">
            <option value="syndicate_owner">Syndicate Owner</option>
            <option value="gm">GM</option>
            <option value="moderator">League Moderator</option>
            <option value="admin">Admin (League Owner)</option>
          </select>
        </div>
        <div><div className="small-label mb-1">Syndicate</div>
          <select value={form.syndicate_id} onChange={(e)=>setForm({...form, syndicate_id:e.target.value})} className="rtrl-input" data-testid="new-user-syn">
            <option value="">—</option>
            {syns.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <button className="rtrl-btn" data-testid="new-user-submit">Create User</button>
      </form>

      <div className="rtrl-card carbon-bg overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 small-label border-b border-surface-line gap-3">
          <div className="col-span-3">Name / Email</div>
          <div className="col-span-3">Role</div>
          <div className="col-span-4">Syndicate Assignment</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {users.map((u, i) => (
          <UserRow key={u.id} u={u} syns={syns} onSave={save} onDelete={handleDelete} idx={i} />
        ))}
      </div>
    </div>
  );
}

function UserRow({ u, syns, onSave, onDelete, idx }) {
  const [edit, setEdit] = useState(u);
  const { user } = useAuth(); // Retrieve active admin logged in
  useEffect(() => setEdit(u), [u.id]);

  const handleRoleChange = (newRole) => {
    if (newRole === "racer") {
      setEdit({ ...edit, role: newRole, syndicate_id: "" });
    } else {
      setEdit({ ...edit, role: newRole });
    }
  };

  const isSelf = user?.id === u.id;

  return (
    <div className="grid grid-cols-12 items-center px-4 py-3 border-b border-surface-line/60 gap-3" data-testid={`user-row-${idx}`}>
      <div className="col-span-3">
        <input className="rtrl-input text-sm" value={edit.display_name || ""} onChange={(e)=>setEdit({...edit, display_name: e.target.value})} placeholder="Display Name" data-testid={`user-edit-name-${idx}`}/>
        <div className="small-label mt-1 lowercase font-mono-tech truncate">{edit.email}</div>
      </div>
      <div className="col-span-3">
        <select className="rtrl-input text-sm" value={edit.role} onChange={(e)=>handleRoleChange(e.target.value)} data-testid={`user-edit-role-${idx}`}>
        <option value="racer">Racer</option>
        <option value="gm">GM</option>
        <option value="syndicate_owner">Syndicate Owner</option>
        <option value="moderator">League Moderator</option>
        <option value="admin">Admin</option>
      </select>
    </div>
    <div className="col-span-4">
      {edit.role !== "racer" ? (
        <select className="rtrl-input text-sm" value={edit.syndicate_id || ""} onChange={(e)=>setEdit({...edit, syndicate_id: e.target.value || ""})} data-testid={`user-edit-syn-${idx}`}>
          <option value="">— No Syndicate —</option>
          {syns.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      ) : (
        <span className="text-white/30 text-xs italic pl-2">No Syndicate Link Required</span>
      )}
    </div>
      <div className="col-span-2 flex justify-end gap-2">
        <button className="rtrl-btn-ghost text-xs px-3 py-1.5" onClick={()=>onSave(edit)} data-testid={`user-save-${idx}`}>Save</button>
        {!isSelf ? (
          <button className="rtrl-btn-ghost text-xs px-3 py-1.5 hover:border-danger hover:text-danger" onClick={()=>onDelete(u.id)} data-testid={`user-delete-${idx}`}>Delete</button>
        ) : (
          <span className="text-white/20 text-xs italic flex items-center pr-2 select-none">Current Session</span>
        )}
      </div>
    </div>
  );
}

function RacersAdmin() {
  const [racers, setRacers] = useState([]);
  const [users, setUsers] = useState([]);
  const [syns, setSyns] = useState([]);

  const load = () => {
    api.get("/racers").then(r=>setRacers(r.data));
    api.get("/auth/users").then(r=>setUsers(r.data.filter(u=>u.role==="racer")));
    api.get("/syndicates").then(r=>setSyns(r.data));
  };
  useEffect(load, []);

  const link = async (rid, uid) => {
    await api.post(`/racers/${rid}/link-user`, { user_id: uid || null });
    toast.success("Linked");
    load();
  };

  const assignSyn = async (r, sid) => {
    await api.patch(`/racers/${r.id}`, { ...r, syndicate_id: sid || null, status: sid ? "active" : "free_agent" });
    toast.success("Syndicate updated");
    load();
  };

  return (
    <div className="mt-6 rtrl-card carbon-bg overflow-hidden">
      <div className="grid grid-cols-12 px-4 py-3 small-label border-b border-surface-line">
        <div className="col-span-3">Racer</div>
        <div className="col-span-3">Syndicate</div>
        <div className="col-span-4">Linked User</div>
        <div className="col-span-2">Status</div>
      </div>
      {racers.map((r,i)=>(
        <div key={r.id} className="grid grid-cols-12 items-center px-4 py-3 border-b border-surface-line/60" data-testid={`racer-row-${i}`}>
          <div className="col-span-3"><div className="text-white">{r.display_name} <span className="font-mono-tech text-white/40">#{r.number ?? "-"}</span></div><div className="small-label">{r.platform || "—"}</div></div>
          <div className="col-span-3">
            <select className="rtrl-input" value={r.syndicate_id || ""} onChange={(e)=>assignSyn(r, e.target.value)} data-testid={`racer-syn-${i}`}>
              <option value="">Free Agent</option>
              {syns.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="col-span-4">
            <select className="rtrl-input" value={r.user_id || ""} onChange={(e)=>link(r.id, e.target.value)} data-testid={`racer-user-${i}`}>
              <option value="">— none —</option>
              {users.map(u=><option key={u.id} value={u.id}>{u.display_name} ({u.email})</option>)}
            </select>
          </div>
          <div className="col-span-2 small-label uppercase text-rtrl-silver">{r.status}</div>
        </div>
      ))}
    </div>
  );
}

function PointsAdmin() {
  const [tables, setTables] = useState({ pod: [], speeder: [] });
  useEffect(() => {
    api.get("/points-tables").then(r=>{
      const map = { pod:[], speeder:[] };
      r.data.forEach(t=> map[t.discipline]=t.points);
      setTables(map);
    });
  }, []);

  const save = async (disc) => {
    await api.put("/points-tables", { discipline: disc, points: tables[disc].map((n)=>parseInt(n,10)||0) });
    toast.success(`${disc} points saved`);
  };

  const setLen = (disc, len) => {
    const arr = Array.from({ length: len }, (_, i) => tables[disc][i] ?? Math.max(len - i, 0));
    setTables({ ...tables, [disc]: arr });
  };

  return (
    <div className="mt-6 grid md:grid-cols-2 gap-6">
      {["pod","speeder"].map((disc)=>(
        <div key={disc} className="rtrl-card carbon-bg p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display uppercase text-3xl" style={{ color: disc==="pod"?"#FF6B00":"#00F0FF" }}>{disc} table</h3>
            <div className="small-label">Positions: {tables[disc].length}</div>
          </div>
          <div className="mt-3 flex gap-2">
            <button className="rtrl-btn-ghost" onClick={()=>setLen(disc, tables[disc].length+1)} data-testid={`pts-add-${disc}`}>+ Row</button>
            <button className="rtrl-btn-ghost" onClick={()=>setLen(disc, Math.max(1, tables[disc].length-1))} data-testid={`pts-rm-${disc}`}>− Row</button>
          </div>
          <div className="mt-4 grid grid-cols-6 gap-2">
            {tables[disc].map((v,i)=>(
              <div key={i}>
                <div className="small-label">P{i+1}</div>
                <input className="rtrl-input font-mono-tech" value={v} onChange={(e)=>{
                  const copy=[...tables[disc]]; copy[i]=e.target.value; setTables({...tables, [disc]:copy});
                }} data-testid={`pts-${disc}-${i}`}/>
              </div>
            ))}
          </div>
          <button className="rtrl-btn mt-6" onClick={()=>save(disc)} data-testid={`pts-save-${disc}`}>Save {disc} points</button>
        </div>
      ))}
    </div>
  );
}

function SettingsAdmin() {
  const [webhook, setWebhook] = useState("");
  const [overrideUnlocked, setOverrideUnlocked] = useState(false);

  useEffect(() => {
    api.get("/settings")
      .then(r => {
        setWebhook(r.data.discord_webhook_url || "");
        setOverrideUnlocked(r.data.roster_override_unlocked || false);
      })
      .catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/settings", {
        discord_webhook_url: webhook,
        roster_override_unlocked: overrideUnlocked
      });
      toast.success("League settings updated successfully");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="mt-6 rtrl-card carbon-bg p-6 max-w-2xl">
      <h3 className="font-display uppercase text-3xl text-white mb-4">League Settings</h3>
      <form onSubmit={save} className="space-y-6">
        <div>
          <div className="small-label mb-2">Discord Webhook URL</div>
          <input
            type="url"
            value={webhook}
            onChange={(e) => setWebhook(e.target.value)}
            className="rtrl-input w-full font-mono-tech text-sm"
            placeholder="https://discord.com/api/webhooks/..."
            data-testid="admin-settings-webhook"
          />
          <p className="text-white/40 text-xs mt-1">Submitted race results will be posted automatically to this channel.</p>
        </div>

        <div className="pt-4 border-t border-surface-line">
          <label className="flex items-center justify-between cursor-pointer gap-4">
            <div>
              <div className="font-display uppercase text-2xl text-white">🔓 Roster Lock Manual Bypass</div>
              <p className="text-white/50 text-xs mt-1">When turned ON, GMs and Syndicate Owners can modify rosters and lineups even during the active 24-hour race freeze.</p>
            </div>
            <div className="relative">
              <input
                type="checkbox"
                checked={overrideUnlocked}
                onChange={(e) => setOverrideUnlocked(e.target.checked)}
                className="sr-only peer"
                data-testid="admin-settings-override-toggle"
              />
              <div className="w-11 h-6 bg-surface-line peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-rtrl-red"></div>
            </div>
          </label>
        </div>

        <button type="submit" className="rtrl-btn w-full justify-center" data-testid="admin-settings-submit">Save Settings</button>
      </form>
    </div>
  );
}

function TracksAdmin() {
  const [tracks, setTracks] = useState([]);
  const [newTrackName, setNewTrackName] = useState("");

  const loadTracks = () => {
    api.get("/tracks").then((res) => setTracks(res.data)).catch(() => {});
  };

  useEffect(() => {
    loadTracks();
  }, []);

  const handleAddTrack = async (e) => {
    e.preventDefault();
    if (!newTrackName.trim()) return;
    try {
      await api.post("/tracks", { name: newTrackName.trim() });
      toast.success("Track successfully added!");
      setNewTrackName("");
      loadTracks();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleSaveTrack = async (tid, name) => {
    try {
      await api.put(`/tracks/${tid}`, { name });
      toast.success("Track name updated");
      loadTracks();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleDeleteTrack = async (tid) => {
    if (!window.confirm("Are you sure you want to delete this track?")) return;
    try {
      await api.delete(`/tracks/${tid}`);
      toast.success("Track deleted");
      loadTracks();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="mt-6 space-y-8 max-w-4xl">
      <div className="rtrl-card carbon-bg p-6">
        <h3 className="font-display uppercase text-3xl text-white mb-2">Race Track Management</h3>
        <p className="small-label text-white/50 mb-4">Maintain a database of up to 20 official race track courses.</p>
        
        <form onSubmit={handleAddTrack} className="flex gap-4">
          <input
            type="text"
            className="rtrl-input flex-1"
            placeholder="e.g. Mos Espa Grand Prix"
            value={newTrackName}
            onChange={(e) => setNewTrackName(e.target.value)}
            disabled={tracks.length >= 20}
            data-testid="admin-track-name-input"
          />
          <button
            type="submit"
            className="rtrl-btn shrink-0"
            disabled={tracks.length >= 20}
            data-testid="admin-track-add-btn"
          >
            {tracks.length >= 20 ? "Limit Reached" : "Add Track"}
          </button>
        </form>
        {tracks.length >= 20 && (
          <p className="text-rtrl-red text-xs mt-2 font-mono-tech">The league permits a maximum of 20 tracks. Delete old tracks to add new ones.</p>
        )}
      </div>

      <div className="space-y-3">
        {tracks.map((t, idx) => (
          <div key={t.id} className="rtrl-card carbon-bg p-4 flex items-center justify-between gap-4 border-l-4 border-rtrl-red">
            <div className="flex-1">
              <input
                type="text"
                className="rtrl-input text-lg font-bold"
                value={t.name}
                onChange={(e) => {
                  const copy = [...tracks];
                  copy[idx].name = e.target.value;
                  setTracks(copy);
                }}
                data-testid={`admin-track-edit-input-${idx}`}
              />
            </div>
            <div className="flex gap-2">
              <button
                className="rtrl-btn-ghost text-xs px-3 py-1.5"
                onClick={() => handleSaveTrack(t.id, t.name)}
                data-testid={`admin-track-save-${idx}`}
              >
                Save
              </button>
              <button
                className="rtrl-btn-ghost text-xs px-3 py-1.5 hover:border-danger hover:text-danger"
                onClick={() => handleDeleteTrack(t.id)}
                data-testid={`admin-track-delete-${idx}`}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
        {tracks.length === 0 && (
          <div className="text-white/40 italic p-8 text-center rtrl-card carbon-bg">No custom tracks registered. All courses default to TBD.</div>
        )}
      </div>
    </div>
  );
}

function SchedulingAdmin() {
  const [seasons, setSeasons] = useState([]);
  const [nights, setNights] = useState([]);
  const [syns, setSyns] = useState([]);
  const [tracks, setTracks] = useState([]);

  // Form states
  const [newSeasonName, setNewSeasonName] = useState("");
  
  const [newNight, setNewNight] = useState({ season_id: "", label: "", scheduled_at: "" });
  
  const [newRound, setNewRound] = useState({
    race_night_id: "",
    round_number: 1,
    discipline: "pod",
    home_syndicate_id: "",
    away_syndicate_id: "",
    track1: "",
    track2: "",
    track3: "",
    track4: ""
  });

  // RNG generator states
  const [rngSeasonId, setRngSeasonId] = useState("");
  const [rngDays, setRngDays] = useState([]);
  const [rngStartDate, setRngStartDate] = useState("");
  const [rngEndDate, setRngEndDate] = useState("");
  const [rngStartTime, setRngStartTime] = useState("20:00");
  const [rngTotalRaces, setRngTotalRaces] = useState("");

  const loadAll = () => {
    api.get("/seasons").then(r => setSeasons(r.data)).catch(() => {});
    api.get("/race-nights").then(r => setNights(r.data)).catch(() => {});
    api.get("/syndicates").then(r => setSyns(r.data)).catch(() => {});
    api.get("/tracks").then(r => setTracks(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleGenerateRngSchedule = async (e) => {
    e.preventDefault();
    if (!rngSeasonId || rngDays.length === 0 || !rngStartDate || !rngStartTime) {
      toast.error("Please fill in all RNG schedule options.");
      return;
    }
    const timezoneOffset = new Date().getTimezoneOffset();
    try {
      const payload = {
        days_of_week: rngDays,
        start_date: rngStartDate,
        start_time: rngStartTime,
        timezone_offset: timezoneOffset,
      };
      if (rngEndDate) {
        payload.end_date = rngEndDate;
      }
      if (rngTotalRaces) {
        payload.total_races_per_team = parseInt(rngTotalRaces, 10);
      }
      const res = await api.post(`/seasons/${rngSeasonId}/generate-schedule`, payload);
      toast.success(`Schedule generated successfully: ${res.data.nights_generated} race nights created!`);
      setRngSeasonId("");
      setRngDays([]);
      setRngStartDate("");
      setRngEndDate("");
      setRngTotalRaces("");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleDeleteSeason = async (sid) => {
    if (!window.confirm("Are you sure you want to permanently delete this season and all its race nights? This cannot be undone.")) return;
    try {
      await api.delete(`/seasons/${sid}`);
      toast.success("Season cleared successfully");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleCreateSeason = async (e) => {
    e.preventDefault();
    if (!newSeasonName.trim()) return;
    try {
      await api.post("/seasons", { name: newSeasonName.trim() });
      toast.success("Season created!");
      setNewSeasonName("");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleCreateNight = async (e) => {
    e.preventDefault();
    if (!newNight.season_id || !newNight.label) {
      toast.error("Please fill in Season and Label");
      return;
    }
    try {
      await api.post("/race-nights", {
        season_id: newNight.season_id,
        label: newNight.label,
        scheduled_at: newNight.scheduled_at ? new Date(newNight.scheduled_at).toISOString() : new Date().toISOString()
      });
      toast.success("Race Night created!");
      setNewNight({ season_id: "", label: "", scheduled_at: "" });
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleCreateRound = async (e) => {
    e.preventDefault();
    if (!newRound.race_night_id || !newRound.home_syndicate_id || !newRound.away_syndicate_id) {
      toast.error("Please select a Race Night and both participating Syndicates.");
      return;
    }
    if (newRound.home_syndicate_id === newRound.away_syndicate_id) {
      toast.error("Home and Away syndicates must be different!");
      return;
    }

    try {
      // 1. Create the Round
      const roundRes = await api.post("/rounds", {
        race_night_id: newRound.race_night_id,
        round_number: parseInt(newRound.round_number, 10),
        discipline: newRound.discipline,
        home_syndicate_id: newRound.home_syndicate_id,
        away_syndicate_id: newRound.away_syndicate_id
      });
      const createdRoundId = roundRes.data.id;

      // 2. Fetch the 4 auto-generated races
      const nightFullRes = await api.get(`/race-nights/${newRound.race_night_id}/full`);
      const matchedRound = (nightFullRes.data.rounds || []).find(x => x.id === createdRoundId);
      if (matchedRound && matchedRound.races) {
        const raceTracks = [newRound.track1, newRound.track2, newRound.track3, newRound.track4];
        for (let i = 0; i < 4; i++) {
          const race = matchedRound.races[i];
          const selectedTrack = raceTracks[i];
          if (race && selectedTrack) {
            await api.post(`/races/${race.id}/results`, {
              entries: [],
              track_name: selectedTrack
            });
          }
        }
      }

      toast.success("Round and Races successfully created with scheduled tracks!");
      setNewRound({
        race_night_id: "",
        round_number: 1,
        discipline: "pod",
        home_syndicate_id: "",
        away_syndicate_id: "",
        track1: "",
        track2: "",
        track3: "",
        track4: ""
      });
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="mt-6 grid md:grid-cols-2 gap-8 max-w-6xl">
      <div className="space-y-8">
        {/* Create Season */}
        <div className="rtrl-card carbon-bg p-6">
          <h3 className="font-display uppercase text-3xl text-white mb-2">Season Management</h3>
          <form onSubmit={handleCreateSeason} className="flex gap-4 items-end mb-6">
            <div className="flex-1">
              <div className="small-label mb-1">Create Season</div>
              <input
                type="text"
                className="rtrl-input"
                placeholder="e.g. Season 2"
                value={newSeasonName}
                onChange={(e) => setNewSeasonName(e.target.value)}
                data-testid="admin-sched-season-name"
              />
            </div>
            <button type="submit" className="rtrl-btn shrink-0" data-testid="admin-sched-season-submit">Create</button>
          </form>

          {seasons.length > 0 && (
            <div className="pt-4 border-t border-surface-line/40 space-y-3">
              <div className="small-label text-rtrl-silver font-semibold">Active Seasons:</div>
              <div className="space-y-2">
                {seasons.map((s, idx) => (
                  <div key={s.id} className="flex items-center justify-between bg-void/35 p-3 border border-surface-line/20 rounded hud-chamfer-sm">
                    <div>
                      <span className="text-white font-body font-bold">{s.name}</span>
                      {s.is_active && <span className="ml-2 text-[10px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-1.5 py-0.5 rounded font-mono-tech">Active</span>}
                    </div>
                    <button
                      onClick={() => handleDeleteSeason(s.id)}
                      className="rtrl-btn-ghost text-xs px-2 py-1 border-danger/30 hover:border-danger hover:text-danger text-danger/80 transition-all font-mono-tech"
                      data-testid={`admin-delete-season-${idx}`}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RNG Schedule Generator */}
        <div className="rtrl-card carbon-bg p-6 border border-rtrl-red/35">
          <h3 className="font-display uppercase text-3xl text-white mb-2">⚡ RNG Schedule Generator</h3>
          <p className="small-label text-white/50 mb-4">Wipes draft schedules and provisions a perfectly balanced double-header schedule.</p>
          <form onSubmit={handleGenerateRngSchedule} className="space-y-4">
            <div>
              <div className="small-label mb-1">Target Season</div>
              <select
                className="rtrl-input"
                value={rngSeasonId}
                onChange={(e) => setRngSeasonId(e.target.value)}
                data-testid="admin-rng-season"
              >
                <option value="">— Select Season —</option>
                {seasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            <div>
              <div className="small-label mb-2">Race Days of the Week</div>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { val: 1, label: "Tue" },
                  { val: 3, label: "Thu" },
                  { val: 5, label: "Sat" },
                  { val: 0, label: "Mon" },
                  { val: 2, label: "Wed" },
                  { val: 4, label: "Fri" },
                  { val: 6, label: "Sun" }
                ].map(day => {
                  const isChecked = rngDays.includes(day.val);
                  return (
                    <button
                      key={day.val}
                      type="button"
                      onClick={() => {
                        setRngDays(prev => 
                          prev.includes(day.val) ? prev.filter(x => x !== day.val) : [...prev, day.val]
                        );
                      }}
                      className={`py-1.5 px-2.5 text-xs font-mono-tech border rounded transition-all ${isChecked ? "bg-rtrl-red/20 border-rtrl-red text-white" : "border-surface-line hover:border-white/20 text-white/60"}`}
                      data-testid={`admin-rng-day-${day.val}`}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="small-label mb-1">Start Date</div>
                <input
                  type="date"
                  className="rtrl-input text-sm"
                  value={rngStartDate}
                  onChange={(e) => setRngStartDate(e.target.value)}
                  data-testid="admin-rng-start-date"
                />
              </div>
              <div>
                <div className="small-label mb-1">End Date (Optional)</div>
                <input
                  type="date"
                  className="rtrl-input text-sm"
                  value={rngEndDate}
                  onChange={(e) => setRngEndDate(e.target.value)}
                  data-testid="admin-rng-end-date"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="small-label mb-1">Start Time</div>
                <input
                  type="time"
                  className="rtrl-input text-sm"
                  value={rngStartTime}
                  onChange={(e) => setRngStartTime(e.target.value)}
                  data-testid="admin-rng-start-time"
                />
              </div>
              <div>
                <div className="small-label mb-1">Total Races per Team (Optional)</div>
                <input
                  type="number"
                  min={1}
                  className="rtrl-input text-sm"
                  value={rngTotalRaces}
                  onChange={(e) => setRngTotalRaces(e.target.value)}
                  placeholder="e.g. 5"
                  data-testid="admin-rng-total-races"
                />
              </div>
            </div>

            <button type="submit" className="rtrl-btn w-full justify-center bg-rtrl-red hover:bg-[#ff2222]" data-testid="admin-rng-submit">
              🚀 Run Generator
            </button>
          </form>
        </div>

        {/* Create Race Night */}
        <div className="rtrl-card carbon-bg p-6">
          <h3 className="font-display uppercase text-3xl text-white mb-2">Schedule Race Night</h3>
          <form onSubmit={handleCreateNight} className="space-y-4">
            <div>
              <div className="small-label mb-1">Assign to Season</div>
              <select
                className="rtrl-input"
                value={newNight.season_id}
                onChange={(e) => setNewNight({ ...newNight, season_id: e.target.value })}
                data-testid="admin-sched-night-season"
              >
                <option value="">— Select Season —</option>
                {seasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <div className="small-label mb-1">Night Label</div>
              <input
                type="text"
                className="rtrl-input"
                placeholder="e.g. Race Night 2 — Thursday"
                value={newNight.label}
                onChange={(e) => setNewNight({ ...newNight, label: e.target.value })}
                data-testid="admin-sched-night-label"
              />
            </div>
            <div>
              <div className="small-label mb-1">Scheduled Date</div>
              <input
                type="datetime-local"
                className="rtrl-input text-sm"
                value={newNight.scheduled_at}
                onChange={(e) => setNewNight({ ...newNight, scheduled_at: e.target.value })}
                data-testid="admin-sched-night-date"
              />
            </div>
            <button type="submit" className="rtrl-btn w-full justify-center" data-testid="admin-sched-night-submit">Schedule Night</button>
          </form>
        </div>
      </div>

      <div className="space-y-8">
        {/* Create Round & Races with Track selections */}
        <div className="rtrl-card carbon-bg p-6">
          <h3 className="font-display uppercase text-3xl text-white mb-2">Add Matchup Round &amp; Races</h3>
          <form onSubmit={handleCreateRound} className="space-y-4">
            <div>
              <div className="small-label mb-1">Select Race Night</div>
              <select
                className="rtrl-input"
                value={newRound.race_night_id}
                onChange={(e) => setNewRound({ ...newRound, race_night_id: e.target.value })}
                data-testid="admin-sched-round-night"
              >
                <option value="">— Select Race Night —</option>
                {nights.map(n => {
                  const s = seasons.find(x => x.id === n.season_id);
                  return <option key={n.id} value={n.id}>{s ? `[${s.name}] ` : ""}{n.label}</option>;
                })}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="small-label mb-1">Round Number</div>
                <select
                  className="rtrl-input"
                  value={newRound.round_number}
                  onChange={(e) => setNewRound({ ...newRound, round_number: e.target.value })}
                  data-testid="admin-sched-round-num"
                >
                  <option value="1">Round 1</option>
                  <option value="2">Round 2</option>
                  <option value="3">Round 3</option>
                  <option value="4">Round 4</option>
                </select>
              </div>
              <div>
                <div className="small-label mb-1">Discipline Class</div>
                <select
                  className="rtrl-input"
                  value={newRound.discipline}
                  onChange={(e) => setNewRound({ ...newRound, discipline: e.target.value })}
                  data-testid="admin-sched-round-disc"
                >
                  <option value="pod">Pod Racing</option>
                  <option value="speeder">Speeder Racing</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="small-label mb-1">Syndicate A</div>
                <select
                  className="rtrl-input"
                  value={newRound.home_syndicate_id}
                  onChange={(e) => setNewRound({ ...newRound, home_syndicate_id: e.target.value })}
                  data-testid="admin-sched-round-home"
                >
                  <option value="">— Syndicate A —</option>
                  {syns.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <div className="small-label mb-1">Syndicate B</div>
                <select
                  className="rtrl-input"
                  value={newRound.away_syndicate_id}
                  onChange={(e) => setNewRound({ ...newRound, away_syndicate_id: e.target.value })}
                  data-testid="admin-sched-round-away"
                >
                  <option value="">— Syndicate B —</option>
                  {syns.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>

            {/* Races track selections */}
            <div className="pt-4 border-t border-surface-line space-y-3">
              <div className="font-display uppercase text-2xl text-white">Track Assignments</div>
              {[1, 2, 3, 4].map(num => (
                <div key={num} className="grid grid-cols-3 gap-2 items-center">
                  <span className="small-label text-rtrl-silver font-semibold">Race {num}</span>
                  <select
                    className="rtrl-input text-xs col-span-2"
                    value={newRound[`track${num}`]}
                    onChange={(e) => setNewRound({ ...newRound, [`track${num}`]: e.target.value })}
                    data-testid={`admin-sched-race-track-${num}`}
                  >
                    <option value="">— Track TBD —</option>
                    {tracks.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                  </select>
                </div>
              ))}
            </div>

            <button type="submit" className="rtrl-btn w-full justify-center" data-testid="admin-sched-round-submit">Create Round &amp; Races</button>
          </form>
        </div>
      </div>
    </div>
  );
}
